import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'
import { HiMiniChevronDown } from 'react-icons/hi2'

import { MiniBadge } from '@/components/common/Badge'
import Button from '@/components/common/Button'
import Checkbox from '@/components/common/Checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import { SearchInput } from '@/components/common/TextInputV2'

import { cn } from '@/helpers/shadcn/utils'

export interface TableFilterOption {
  label: string
  value: string
  /** Optional leading visual (facet icon, author avatar). */
  icon?: React.ReactNode
}

interface DataTableFacetDropdownProps {
  label: string
  value: string[]
  options: TableFilterOption[]
  onCheckedChange: (option: string, checked: boolean) => void
}

const handleCloseAutoFocus = (e: Event) => {
  e.preventDefault()
}

const ITEM_HEIGHT = 32 // Estimated height for each item

interface OptionButtonProps {
  option: TableFilterOption
  isSelected: boolean
  isFocused: boolean
  onClick: () => void
}

const OptionButton: React.FC<OptionButtonProps> = ({
  option,
  isSelected,
  isFocused,
  onClick,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 w-full px-2 py-1.5 text-left text-sm',
        'hover:bg-primary-100 dark:hover:bg-primary-800',
        'focus:outline-none',
        isFocused && 'bg-primary-100 dark:bg-primary-800',
        isSelected && !isFocused && 'bg-primary-50 dark:bg-primary-800/50',
      )}
    >
      <Checkbox
        id={`filter-${option.value}`}
        checked={isSelected}
        childrenOnly={true}
        readOnly={true}
      />
      {option.icon != null && (
        <span className="flex flex-shrink-0 items-center">{option.icon}</span>
      )}
      <span className="truncate text-primary-900 dark:text-primary-100">
        {option.label}
      </span>
    </button>
  )
}

const estimateSize = () => ITEM_HEIGHT

export const DataTableFacetDropdown: React.FC<DataTableFacetDropdownProps> = ({
  label,
  value,
  options,
  onCheckedChange,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [isOpen, setIsOpen] = useState<boolean>(false)
  // -1 means search input is focused, >= 0 means item at that index is focused
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  // Use state instead of ref so virtualizer re-initializes when element mounts
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null,
  )

  const filteredOptions = useMemo(() => {
    const sortedOptions = options
      .slice()
      .sort((a, b) => a.label.localeCompare(b.label))
    if (!searchQuery.trim()) {
      return sortedOptions
    }
    const query = searchQuery.toLowerCase()
    return sortedOptions.filter((option) =>
      option.label.toLowerCase().includes(query),
    )
  }, [options, searchQuery])

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollContainer,
    estimateSize,
    overscan: 10,
  })

  const handleCheckedChange = useCallback(
    (optionValue: string) => () => {
      const isChecked = value.includes(optionValue)
      onCheckedChange(optionValue, !isChecked)
    },
    [onCheckedChange, value],
  )

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    [],
  )

  const handleSearchClick = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      e.stopPropagation()
    },
    [],
  )

  const handleSearchFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.stopPropagation()
      setFocusedIndex(-1)
    },
    [],
  )

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Prevent Radix typeahead from receiving key events when typing in the input
      e.stopPropagation()
      if (e.key === 'ArrowDown' && filteredOptions.length > 0) {
        e.preventDefault()
        setFocusedIndex(0)
        scrollContainer?.focus()
      }
    },
    [filteredOptions.length, scrollContainer],
  )

  const handleListKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      e.stopPropagation()

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setFocusedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev,
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (focusedIndex === 0) {
          // Go back to search input
          setFocusedIndex(-1)
          inputRef.current?.focus()
        } else {
          setFocusedIndex((prev) => Math.max(0, prev - 1))
        }
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          const option = filteredOptions[focusedIndex]
          const isChecked = value.includes(option.value)
          onCheckedChange(option.value, !isChecked)
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setIsOpen(false)
      }
    },
    [filteredOptions, focusedIndex, onCheckedChange, value],
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      if (open) {
        setTimeout(() => {
          inputRef.current?.focus()
        }, 150)
      } else {
        setSearchQuery('')
        setFocusedIndex(-1)
      }
    },
    [setSearchQuery],
  )

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
      virtualizer.scrollToIndex(focusedIndex, { align: 'auto' })
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [focusedIndex, filteredOptions.length])

  // Reset focused index when search changes
  useEffect(() => {
    setFocusedIndex(-1)
  }, [searchQuery.length])

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild={true}>
        <Button variant="outline-compact" className="active:scale-100">
          <span>{label}</span>
          {value.length > 0 && (
            <MiniBadge
              count={value.length}
              variant="soft"
              className="ml-[3px] text-[9px]"
            />
          )}
          <HiMiniChevronDown
            size={14}
            className="text-primary-900 dark:text-primary-100"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-72 p-0"
        onCloseAutoFocus={handleCloseAutoFocus}
        autoFocus={false}
      >
        {options.length > 0 ? (
          <div className="flex flex-col">
            <div className="px-2 pt-2 pb-2 border-b border-primary-200 dark:border-primary-800">
              <SearchInput
                ref={inputRef}
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleSearchChange}
                onClick={handleSearchClick}
                onFocus={handleSearchFocus}
                onKeyDown={handleSearchKeyDown}
                autoComplete="off"
              />
            </div>
            {filteredOptions.length > 0 ? (
              <div
                ref={setScrollContainer}
                className="max-h-[300px] overflow-y-auto overflow-x-hidden focus:outline-none"
                tabIndex={0}
                onKeyDown={handleListKeyDown}
              >
                <div
                  style={{
                    // Use estimated height as fallback when virtualizer hasn't calculated yet
                    height: `${Math.max(virtualizer.getTotalSize(), filteredOptions.length * ITEM_HEIGHT)}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {/* If virtualizer has no items yet, render them statically */}
                  {virtualizer.getVirtualItems().length > 0
                    ? virtualizer.getVirtualItems().map((virtualItem) => {
                        const option = filteredOptions[virtualItem.index]
                        const isSelected = value.includes(option.value)
                        const isFocused = virtualItem.index === focusedIndex
                        return (
                          <div
                            key={option.value}
                            data-index={virtualItem.index}
                            ref={virtualizer.measureElement}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              transform: `translateY(${virtualItem.start}px)`,
                            }}
                          >
                            <OptionButton
                              option={option}
                              isSelected={isSelected}
                              isFocused={isFocused}
                              onClick={handleCheckedChange(option.value)}
                            />
                          </div>
                        )
                      })
                    : // Fallback: render first batch statically while virtualizer initializes
                      filteredOptions
                        .slice(0, Math.ceil(300 / ITEM_HEIGHT) + 5)
                        .map((option, index) => {
                          const isSelected = value.includes(option.value)
                          const isFocused = index === focusedIndex
                          return (
                            <div
                              key={option.value}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${index * ITEM_HEIGHT}px)`,
                              }}
                            >
                              <OptionButton
                                option={option}
                                isSelected={isSelected}
                                isFocused={isFocused}
                                onClick={handleCheckedChange(option.value)}
                              />
                            </div>
                          )
                        })}
                </div>
              </div>
            ) : (
              <div className="text-sm text-primary-500 dark:text-primary-400 py-3 px-3 text-start break-words">
                No options found matching &quot;{searchQuery}&quot;
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-primary-500 dark:text-primary-400 py-3 px-3 text-start">
            No options available for{' '}
            <span className="font-medium">{label}</span>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
